import {
  FulfillmentStatus,
  OrderAction,
  OrderDetailsFragment,
  OrderStatus,
} from "@dashboard/graphql";
import { MessageDescriptor } from "react-intl";

import { isPickupReadyNotified, isStorePickup } from "../../utils/delivery";
import { orderProgressStepperMessages } from "./messages";

export interface OrderProgressStep {
  key: string;
  label: MessageDescriptor;
  completed: boolean;
}

// Some Saleor deployments have no invoicing app/plugin, so `invoiceRequest`
// always fails with NO_INVOICE_PLUGIN. In that case staff can instead mark the
// invoice step as done by hand; the choice is persisted as an order metadata
// flag so it survives reloads and is shared across the team.
export const INVOICE_MARKED_DONE_KEY = "invoiceMarkedDone";

export const isInvoiceManuallyMarked = (order: OrderDetailsFragment | undefined): boolean =>
  (order?.metadata ?? []).some(
    entry => entry?.key === INVOICE_MARKED_DONE_KEY && entry?.value === "true",
  );

// Saleor has no "customer received the goods" status short of a full fulfillment,
// so staff can flag the final step done by hand. Like the invoice mark, the choice
// is persisted as an order metadata flag so it survives reloads and is shared.
export const ORDER_RECEIVED_MARKED_KEY = "orderReceivedMarked";

export const isReceivedManuallyMarked = (order: OrderDetailsFragment | undefined): boolean =>
  (order?.metadata ?? []).some(
    entry => entry?.key === ORDER_RECEIVED_MARKED_KEY && entry?.value === "true",
  );

// Saleor has no native "packaging / shipping / received" states, so the steps
// below are approximated from data that already exists on the order:
//  - Submitted:   the order exists (it left draft state)
//  - Payment:     the order is marked paid
//  - Invoice:     at least one invoice was generated
//  - Preparation: every line is packed into a fulfillment (nothing left to fulfill)
//  - Shipping:    the order is fully packed & dispatched — "Pack & ship" (skipped
//                 for in-store pickup). A tracking number is optional and does NOT
//                 gate this step.
//  - Ready:       in-store pickup only, replaces Shipping: staff told the customer
//                 the order is prepared and can be collected
//  - Received:    staff manually confirm the customer got the goods
//
// Note: the native FULFILLED status is NOT treated as "received". In Saleor an
// order becomes FULFILLED the moment its lines are packed into a fulfillment —
// before any tracking number and well before the customer actually receives the
// parcel. Keying "received" off FULFILLED made the Preparation/fulfil action jump
// the whole flow straight to complete, skipping Shipping and the receipt
// confirmation. The final step therefore completes only via the explicit manual
// mark (persisted as an order metadata flag).
export const getOrderProgressSteps = (
  order: OrderDetailsFragment | undefined,
): OrderProgressStep[] => {
  const isPaid = !!order?.isPaid;
  // A real invoice (from an invoicing app) or a manual "invoice done" flag both
  // count the step as complete.
  const hasInvoice = (order?.invoices?.length ?? 0) > 0 || isInvoiceManuallyMarked(order);
  const fulfillments = order?.fulfillments ?? [];
  const hasFulfillment = fulfillments.length > 0;
  const hasUnfulfilledLines = (order?.lines ?? []).some(line => (line?.quantityToFulfill ?? 0) > 0);
  // "Pack & ship": once every line is packed into a fulfillment the order counts as
  // shipped. A tracking number is optional (it can still be added later and is used
  // to resend the shipping email), so it does NOT gate the Shipping step.
  const isShipped = hasFulfillment && !hasUnfulfilledLines;
  // Only an explicit manual "received" mark completes the final step — the native
  // FULFILLED status means packed/dispatched, not received (see note above).
  const isReceived = isReceivedManuallyMarked(order);
  // Click-and-collect orders are picked up in store, so there is no parcel to
  // ship or track — the Shipping step doesn't apply to them. The storefront sells
  // in-store pickup as an ordinary shipping method, so the backend's deliveryType
  // is what recognises it.
  const isClickAndCollect =
    order?.deliveryMethod?.__typename === "Warehouse" || isStorePickup(order?.deliveryType);

  return [
    {
      key: "submitted",
      label: orderProgressStepperMessages.submitted,
      // An unconfirmed order exists but still needs staff to confirm it, so the
      // first step stays active until confirmation happens.
      completed: !!order && order.status !== OrderStatus.UNCONFIRMED,
    },
    {
      key: "payment",
      label: orderProgressStepperMessages.payment,
      completed: isPaid,
    },
    {
      key: "invoice",
      label: orderProgressStepperMessages.invoice,
      completed: hasInvoice,
    },
    {
      key: "preparation",
      label: orderProgressStepperMessages.preparation,
      // Kept "active" through partial fulfillment so the Fulfill action stays
      // available in the stepper until every line is packed.
      completed: hasFulfillment && !hasUnfulfilledLines,
    },
    // In-store pickup has no parcel to track: the customer is told the order is
    // ready to collect instead.
    ...(isClickAndCollect
      ? [
          {
            key: "readyForPickup",
            label: orderProgressStepperMessages.readyForPickup,
            completed: isPickupReadyNotified(order?.metadata),
          },
        ]
      : [
          {
            key: "shipping",
            label: orderProgressStepperMessages.shipping,
            completed: isShipped,
          },
        ]),
    {
      key: "received",
      label: isClickAndCollect
        ? orderProgressStepperMessages.collected
        : orderProgressStepperMessages.received,
      completed: isReceived,
    },
  ];
};

// The active step is the first one that is not yet completed. If every step is
// completed the order is done, so there is no active step.
export const getActiveStepIndex = (steps: OrderProgressStep[]): number =>
  steps.findIndex(step => !step.completed);

export interface OrderPrimaryAction {
  label: MessageDescriptor;
  onClick: () => void;
  disabled: boolean;
  disabledReason?: MessageDescriptor;
  testId?: string;
}

export interface OrderPrimaryActionHandlers {
  onConfirm?: () => void;
  onMarkAsPaid: () => void;
  // Primary invoice action: request an invoice from the invoicing app/plugin.
  onInvoiceGenerate: () => void;
  // Fallback for deployments with no invoicing app: toggles the manual "invoice
  // done" flag. `marked` is the target state, so the stepper can both mark the
  // step done and let staff undo it.
  onInvoiceMarkToggle: (marked: boolean) => void;
  onOrderFulfill: () => void;
  onTrackingAdd: (fulfillmentId: string) => void;
  onApprove: (fulfillmentId: string) => void;
  // Manual completion of the final step for orders that never reach the native
  // FULFILLED status. Opening the received dialog (to choose the optional email)
  // is the caller's concern; `marked` is the target state so staff can also undo.
  onReceivedMarkToggle: (marked: boolean) => void;
  // In-store pickup: marks the order ready to collect. Opening the dialog (to
  // choose the optional email) is the caller's concern; `marked` is the target
  // state so staff can also undo. Absent where the step can't be reached.
  onPickupReadyMarkToggle?: (marked: boolean) => void;
  // Resend the customer email for an already-completed step. Invoice and shipping
  // use native Saleor mutations; confirmation and payment go through the custom
  // backend `orderSendNotification` mutation.
  onResendInvoice: () => void;
  onResendShipping: () => void;
  onResendConfirmation: () => void;
  onResendPayment: () => void;
  // Opens the payment-reminder dialog (mode selection happens there) for an
  // order that is still unpaid.
  onSendPaymentReminder: () => void;
  notAllowedToFulfillUnpaid: boolean;
}

// Resolves the single "what to do next" action for the active step, so every
// order-progression control lives in one guided place instead of being spread
// across the page.
export const getPrimaryAction = (
  order: OrderDetailsFragment | undefined,
  steps: OrderProgressStep[],
  handlers: OrderPrimaryActionHandlers,
): OrderPrimaryAction | null => {
  const activeIndex = getActiveStepIndex(steps);

  if (!order || activeIndex === -1) {
    return null;
  }

  const activeKey = steps[activeIndex].key;
  const fulfillments = order.fulfillments ?? [];

  switch (activeKey) {
    case "submitted": {
      // The submitted step is only ever active for an unconfirmed order, which
      // staff confirm before proceeding.
      if (!handlers.onConfirm) {
        return null;
      }

      return {
        label: orderProgressStepperMessages.confirmOrderAction,
        onClick: handlers.onConfirm,
        disabled: false,
        testId: "stepper-confirm-order",
      };
    }
    case "payment": {
      const canMarkAsPaid = (order.actions ?? []).includes(OrderAction.MARK_AS_PAID);

      if (!canMarkAsPaid) {
        // Payment is settling through a gateway — nothing for staff to click.
        return null;
      }

      return {
        label: orderProgressStepperMessages.markAsPaidAction,
        onClick: handlers.onMarkAsPaid,
        disabled: false,
        testId: "stepper-mark-as-paid",
      };
    }
    case "invoice":
      // Primary path: generate a real invoice via the invoicing app. When no app
      // is configured this fails with NO_INVOICE_PLUGIN, and staff use the manual
      // "Mark as done manually" fallback rendered alongside this action.
      return {
        label: orderProgressStepperMessages.generateInvoiceAction,
        onClick: handlers.onInvoiceGenerate,
        disabled: false,
        testId: "stepper-generate-invoice",
      };
    case "preparation":
      return {
        label: orderProgressStepperMessages.fulfillAction,
        onClick: handlers.onOrderFulfill,
        disabled: handlers.notAllowedToFulfillUnpaid,
        disabledReason: handlers.notAllowedToFulfillUnpaid
          ? orderProgressStepperMessages.cannotFulfillUnpaid
          : undefined,
        testId: "stepper-fulfill",
      };
    case "shipping": {
      // Defensive: since shipping now completes together with preparation (tracking
      // is optional), this step is not normally the active one. Kept so adding a
      // tracking number still works if the step logic changes.
      const target = fulfillments.find(fulfillment => !fulfillment?.trackingNumber);

      if (!target) {
        return null;
      }

      return {
        label: orderProgressStepperMessages.addTrackingAction,
        onClick: () => handlers.onTrackingAdd(target.id),
        disabled: false,
        testId: "stepper-add-tracking",
      };
    }
    case "readyForPickup": {
      if (!handlers.onPickupReadyMarkToggle) {
        return null;
      }

      const onPickupReadyMarkToggle = handlers.onPickupReadyMarkToggle;

      return {
        label: orderProgressStepperMessages.markPickupReadyAction,
        onClick: () => onPickupReadyMarkToggle(true),
        disabled: false,
        testId: "stepper-mark-pickup-ready",
      };
    }
    case "received": {
      const target = fulfillments.find(
        fulfillment => fulfillment?.status === FulfillmentStatus.WAITING_FOR_APPROVAL,
      );

      if (!target) {
        return null;
      }

      return {
        label: orderProgressStepperMessages.approveAction,
        onClick: () => handlers.onApprove(target.id),
        disabled: false,
        testId: "stepper-approve",
      };
    }
    default:
      return null;
  }
};
